import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useConsignee,
  useRevokeConsignee,
  useUpdateConsignee,
  buildInviteLink,
} from '../../hooks/useConsignees'
import {
  useConsigneeInventory,
  useHandoffProducts,
} from '../../hooks/useConsigneeInventory'
import {
  useSettleConsignee,
  computeUnsettledTotals,
} from '../../hooks/useConsigneeSettlements'
import { useProducts } from '../../hooks/useProducts'
import { useSales } from '../../hooks/useSales'
import { BackLink } from '../../components/BackLink'
import { Card, Skeleton, Chip } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import { useToast } from '../../components/ToastContext'

export default function ConsigneeDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: consignee, isLoading } = useConsignee(id)
  const { data: inventory } = useConsigneeInventory(id)
  const { data: products } = useProducts()
  const { data: sales } = useSales()
  const handoff = useHandoffProducts()
  const settle = useSettleConsignee()
  const update = useUpdateConsignee()
  const revoke = useRevokeConsignee()

  const [productId, setProductId] = useState('')
  const [qty, setQty] = useState('1')
  const [handoffError, setHandoffError] = useState('')
  const [settleError, setSettleError] = useState('')

  if (isLoading || !consignee) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <BackLink fallback="/settings/consignees" />
        <Skeleton height={120} />
      </div>
    )
  }

  const productById = new Map((products ?? []).map((p) => [p.id, p]))
  const totals = computeUnsettledTotals(sales ?? [], consignee.id)
  const isRevoked = !!consignee.revoked_at

  const handleHandoff = async (signedQty: number) => {
    setHandoffError('')
    if (!productId) {
      setHandoffError('Pick a product.')
      return
    }
    try {
      await handoff.mutateAsync({
        consignee_id: consignee.id,
        product_id: productId,
        quantity: signedQty,
      })
      toast({
        tone: 'success',
        text: `${signedQty > 0 ? 'Handed off' : 'Took back'} ${Math.abs(signedQty)} unit${Math.abs(signedQty) === 1 ? '' : 's'}`,
      })
      setQty('1')
    } catch (e) {
      setHandoffError((e as Error).message)
    }
  }

  const handleSettle = async () => {
    setSettleError('')
    try {
      await settle.mutateAsync({ consignee_id: consignee.id })
      toast({
        tone: 'success',
        text: `Settled ${formatMoney(totals.ownerReceives)} from ${totals.count} sale${totals.count === 1 ? '' : 's'}`,
      })
    } catch (e) {
      setSettleError((e as Error).message)
    }
  }

  const handleRevoke = async () => {
    if (!window.confirm(`Revoke ${consignee.name}'s access? They'll no longer be able to log sales.`)) return
    await revoke.mutateAsync(consignee.id)
    navigate('/settings/consignees')
  }

  const handleUpdateCut = async () => {
    const next = window.prompt(`New cut % for ${consignee.name}:`, String(consignee.cut_pct))
    if (next == null) return
    const pct = Number(next)
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      window.alert('Cut must be a number between 0 and 100.')
      return
    }
    await update.mutateAsync({ id: consignee.id, cut_pct: pct })
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 640 }}>
      <BackLink fallback="/settings/consignees" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <h1 style={{ margin: 0 }}>{consignee.name}</h1>
        {isRevoked && <Chip tone="danger">Revoked</Chip>}
        {!isRevoked && consignee.accepted_at && <Chip tone="success">Active</Chip>}
        {!isRevoked && !consignee.accepted_at && <Chip tone="warning">Pending invite</Chip>}
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        {consignee.invite_email} · cut {consignee.cut_pct}%{' '}
        <button
          type="button"
          onClick={handleUpdateCut}
          style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-xs)', marginLeft: 'var(--space-1)' }}
        >
          edit
        </button>
      </p>

      {!consignee.accepted_at && !isRevoked && (
        <Card style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>
            Awaiting acceptance
          </div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
            Send them this link if they haven&apos;t gotten it yet:
          </p>
          <input
            readOnly
            value={consignee.invite_token ? buildInviteLink(consignee.invite_token) : ''}
            onFocus={(e) => e.currentTarget.select()}
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface-2)',
              color: 'var(--color-text)',
              fontSize: 'var(--font-size-xs)',
              fontFamily: 'var(--font-mono)',
            }}
          />
        </Card>
      )}

      {/* ── Inventory ── */}
      <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-3)' }}>
        Inventory
      </h2>
      <Card style={{ marginBottom: 'var(--space-4)' }}>
        {(inventory ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Nothing handed off yet. Use the form below to give them some.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(inventory ?? []).map((row) => {
              const product = productById.get(row.product_id)
              return (
                <li
                  key={row.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 'var(--space-2) 0',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <span>{product?.name ?? 'Unknown product'}</span>
                  <span style={{ fontWeight: 700 }}>{row.quantity_on_hand}</span>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {/* ── Hand off / take back ── */}
      {!isRevoked && (
        <>
          <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-3)' }}>
            Hand off / take back
          </h2>
          <Card style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                style={{
                  flex: 2,
                  minWidth: 160,
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                }}
              >
                <option value="">Pick a product…</option>
                {(products ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="qty"
                style={{
                  width: 72,
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                onClick={() => handleHandoff(Number(qty) || 0)}
                disabled={handoff.isPending}
                style={{
                  flex: 1,
                  padding: 'var(--space-3)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Hand off
              </button>
              <button
                type="button"
                onClick={() => handleHandoff(-(Number(qty) || 0))}
                disabled={handoff.isPending}
                style={{
                  flex: 1,
                  padding: 'var(--space-3)',
                  color: 'var(--color-danger)',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-sm)',
                  background: 'transparent',
                }}
              >
                Take back
              </button>
            </div>
            {handoffError && (
              <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>
                {handoffError}
              </p>
            )}
          </Card>
        </>
      )}

      {/* ── Settle up ── */}
      <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-3)' }}>
        Settle up
      </h2>
      <Card style={{ marginBottom: 'var(--space-4)' }}>
        {totals.count === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            All caught up — no unsettled sales.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Total sold</span>
              <span style={{ fontWeight: 600 }}>{formatMoney(totals.totalSold)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)' }}>
              <span>{consignee.name}&apos;s cut ({consignee.cut_pct}%)</span>
              <span>−{formatMoney(totals.momCut)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: '1px solid var(--color-border)',
                marginTop: 'var(--space-2)',
                paddingTop: 'var(--space-2)',
                fontWeight: 700,
                color: 'var(--color-success-text)',
              }}
            >
              <span>You receive</span>
              <span>{formatMoney(totals.ownerReceives)}</span>
            </div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-2)' }}>
              From {totals.count} sale{totals.count === 1 ? '' : 's'}.
            </p>
            <button
              type="button"
              onClick={handleSettle}
              disabled={settle.isPending}
              style={{
                marginTop: 'var(--space-3)',
                width: '100%',
                padding: 'var(--space-3)',
                background: 'var(--color-success)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
              }}
            >
              {settle.isPending ? 'Settling…' : 'Mark settled'}
            </button>
            {settleError && (
              <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>
                {settleError}
              </p>
            )}
          </>
        )}
      </Card>

      {/* ── Danger zone ── */}
      {!isRevoked && (
        <Card>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>
            Danger zone
          </div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
            Revoking removes their ability to log sales. Their historical sales stay in your data.
          </p>
          <button
            type="button"
            onClick={handleRevoke}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              background: 'transparent',
            }}
          >
            Revoke access
          </button>
        </Card>
      )}
    </div>
  )
}
